/**
 * MSVplus Enterprise - Theme Store
 *
 * Dieser Store verwaltet das Theme-System der Anwendung (Hell, Dunkel, System).
 * Er speichert die Theme-Präferenz des Benutzers in localStorage und wendet
 * das Theme auf die gesamte Anwendung an.
 *
 * @version 1.0.0
 * @author MiniMax Agent
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * Verfügbare Theme-Optionen
 */
export type ThemeOption = 'light' | 'dark' | 'system'

/**
 * Interface für Theme-Konfiguration
 */
export interface ThemeConfig {
  name: ThemeOption
  label: string
  icon: string
  description: string
}

/**
 * Theme Store Definition
 * Verwendung der Composition API mit Vue 3
 */
export const useThemeStore = defineStore('theme', () => {
  // State
  const currentTheme = ref<ThemeOption>('system')
  const systemPreference = ref<'light' | 'dark'>('light')

  /**
   * Verfügbare Themes mit Konfiguration
   */
  const availableThemes: ThemeConfig[] = [
    {
      name: 'light',
      label: 'Hell',
      icon: 'SunIcon',
      description: 'Helles Design für helle Umgebungen'
    },
    {
      name: 'dark',
      label: 'Dunkel',
      icon: 'MoonIcon',
      description: 'Dunkles Design für dunkle Umgebungen'
    },
    {
      name: 'system',
      label: 'System',
      icon: 'ComputerIcon',
      description: 'Automatisch an Systemeinstellungen anpassen'
    }
  ]

  // Getters
  /**
   * Prüft ob aktuelles Theme hell ist
   */
  const isLight = computed(() => {
    if (currentTheme.value === 'system') {
      return systemPreference.value === 'light'
    }
    return currentTheme.value === 'light'
  })

  /**
   * Prüft ob aktuelles Theme dunkel ist
   */
  const isDark = computed(() => {
    if (currentTheme.value === 'system') {
      return systemPreference.value === 'dark'
    }
    return currentTheme.value === 'dark'
  })

  /**
   * Gibt das effektive Theme zurück (auflöst system-Thema)
   */
  const effectiveTheme = computed(() => {
    if (currentTheme.value === 'system') {
      return systemPreference.value
    }
    return currentTheme.value
  })

  // Actions
  /**
   * Setzt das Theme auf den angegebenen Wert
   * @param theme - Das zu setzende Theme
   */
  function setTheme(theme: ThemeOption): void {
    currentTheme.value = theme
    applyTheme(theme)
    saveThemeToStorage(theme)
  }

  /**
   * Wechselt zum nächsten Theme in der Liste
   */
  function toggleTheme(): void {
    const currentIndex = availableThemes.findIndex(t => t.name === currentTheme.value)
    const nextIndex = (currentIndex + 1) % availableThemes.length
    setTheme(availableThemes[nextIndex].name)
  }

  /**
   * Wendet das Theme auf das HTML-Element an
   * @param theme - Das anzuwendende Theme
   */
  function applyTheme(theme: ThemeOption): void {
    const htmlElement = document.documentElement

    // Alle Theme-Klassen entfernen
    htmlElement.classList.remove('light', 'dark')

    if (theme === 'system') {
      // System-Theme verwenden
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      htmlElement.classList.add(prefersDark ? 'dark' : 'light')
      systemPreference.value = prefersDark ? 'dark' : 'light'
    } else {
      // Explizites Theme verwenden
      htmlElement.classList.add(theme)
    }
  }

  /**
   * Lädt das Theme aus localStorage
   */
  async function loadThemeFromStorage(): Promise<void> {
    try {
      const savedTheme = localStorage.getItem('msvplus-theme') as ThemeOption | null

      if (savedTheme && isValidTheme(savedTheme)) {
        currentTheme.value = savedTheme
      } else {
        // Standard: System-Theme
        currentTheme.value = 'system'
      }

      // System-Präferenz initial setzen
      updateSystemPreference()

      // Theme anwenden
      applyTheme(currentTheme.value)

      // Listener für System-Theme-Änderungen registrieren
      registerSystemThemeListener()
    } catch (error) {
      console.error('Fehler beim Laden des Themes:', error)
      currentTheme.value = 'system'
      applyTheme('system')
    }
  }

  /**
   * Speichert das Theme in localStorage
   * @param theme - Das zu speichernde Theme
   */
  function saveThemeToStorage(theme: ThemeOption): void {
    try {
      localStorage.setItem('msvplus-theme', theme)
    } catch (error) {
      console.error('Fehler beim Speichern des Themes:', error)
    }
  }

  /**
   * Aktualisiert die System-Präferenz
   */
  function updateSystemPreference(): void {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    systemPreference.value = prefersDark ? 'dark' : 'light'
  }

  /**
   * Registriert einen Listener für System-Theme-Änderungen
   */
  function registerSystemThemeListener(): void {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    mediaQuery.addEventListener('change', (e) => {
      systemPreference.value = e.matches ? 'dark' : 'light'

      // Nur anwenden wenn System-Theme aktiv ist
      if (currentTheme.value === 'system') {
        applyTheme('system')
      }
    })
  }

  /**
   * Validiert ob ein Theme gültig ist
   * @param theme - Das zu validierende Theme
   * @returns true wenn gültig, false otherwise
   */
  function isValidTheme(theme: string): theme is ThemeOption {
    return ['light', 'dark', 'system'].includes(theme)
  }

  /**
   * Setzt das Theme auf Standard zurück
   */
  function resetToDefault(): void {
    setTheme('system')
  }

  return {
    // State
    currentTheme,
    systemPreference,

    // Getters
    availableThemes,
    isLight,
    isDark,
    effectiveTheme,

    // Actions
    setTheme,
    toggleTheme,
    loadThemeFromStorage,
    applyTheme,
    resetToDefault,
    updateSystemPreference
  }
})
